import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { getApiBaseUrl } from '../../config/api';
import { formatGoalTime } from '../race/timeInput';

/**
 * Race-weekend CTA for /joke: a free race plan, then a web signup.
 *
 * Why this replaces the sms: JOKE button on race weekend: 12 of 14 visitors revealed the
 * punchline and 2 tapped the button, and the founder sees people open Messages and never hit
 * send. Every Chicago punchline names a pacing problem, so the next beat SOLVES it: goal time
 * in, the athlete engine's plan out (GET /api/public/race-plan, utils/race_plan.py, the same
 * numbers a paying athlete sees). Value before the ask.
 *
 * The ask is the web signup (POST /api/public/register), where Mattr texts FIRST, so nobody has
 * to compose a message to a stranger's number. Two hard rules:
 *   - The consent checkboxes are the /onboarding form's, WORD FOR WORD. The checkbox IS the
 *     consent on this path (no YES reply), and 10DLC review compares CTA copy to what we send.
 *   - Never promise the plan by text. The first text is the onboarding welcome; the plan lives
 *     on this page. "Text me my plan" would be a claim the backend doesn't keep.
 */

const track = (event, params) => {
  if (typeof window !== 'undefined' && window.gtag) window.gtag('event', event, params);
};

const mi = (v) => `${Math.round(Number(v) * 10) / 10}`;

/** Mirrors RacePlan.jsx segRange so the stranger's card reads like the athlete's page. */
export const segRange = (s, dist) => {
  const toFinish = s.to_mi >= dist - 0.05;
  const from = Number.isInteger(s.from_mi) ? s.from_mi + 1 : s.from_mi;
  if (toFinish && from > dist - 0.05) return `Last ${mi(dist - s.from_mi)} mi`;
  if (toFinish) return `Mile ${mi(from)} to finish`;
  if (from >= s.to_mi) return `Mile ${mi(s.to_mi)}`;
  return `Miles ${mi(from)} to ${mi(s.to_mi)}`;
};

const CHECKPOINT_LABEL = { HALF: 'Half', FINISH: 'Finish', '20 MI': '20 mi', '10 MI': '10 mi' };

/**
 * The card leads with THIS runner's race at THIS course: each landmark (race DB course_notes,
 * sourced) with the clock they'll reach it on. The first version led with three generic rows
 * (settle in / lock in / run what's left) that read the same for any city and any goal; the
 * founder's verdict was "too generic". The pace numbers moved to one compact strip.
 * Races without course notes fall back to the checkpoint line.
 */
function PlanCard({ plan }) {
  const dist = plan.race.distance_mi;
  const fuel = plan.fuel;
  const landmarks = plan.landmarks || [];
  const courseGel = fuel?.course_gels?.product;
  return (
    <section className="race-card" aria-label={`Your ${plan.race.name} plan`}>
      <p className="race-card-kicker">Your {plan.race.name}</p>
      <p className="race-card-headline">
        {plan.plan.finish} <span>· {plan.plan.avg_pace}/mi</span>
      </p>

      <ul className="race-card-paces" aria-label="Pace by section">
        {plan.segments.map((s) => (
          <li key={`${s.from_mi}-${s.to_mi}`}>
            <span className="race-card-paces-range">{segRange(s, dist)}</span>
            <span className="race-card-pace">{s.pace}</span>
          </li>
        ))}
      </ul>

      {landmarks.length > 0 ? (
        <ol className="race-card-timeline" aria-label="Your race, mile by mile">
          {landmarks.map((l) => (
            <li key={`${l.mile}-${l.label}`}>
              <span className="race-card-clock">{l.clock}</span>
              <span className="race-card-stop">
                <strong>Mile {mi(l.mile)} · {l.label}</strong>
                <span>{l.note}</span>
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="race-card-checkpoints">
          {plan.checkpoints.map((c) => `${CHECKPOINT_LABEL[c.label] || c.label} ${c.clock}`).join('  ·  ')}
        </p>
      )}

      {fuel?.needed && (
        <div className="race-card-fuel">
          <p>
            <strong>Gels ({fuel.gel_g} g):</strong> mile{' '}
            {fuel.gels.map((g) => `${mi(g.mile)}${g.course ? '*' : ''}`).join(' · ')}
            {fuel.before_start ? ', plus one before the start' : ''}.
          </p>
          {courseGel && fuel.gels.some((g) => g.course) && (
            <p className="race-card-fine">* {courseGel} is handed out on course there.</p>
          )}
        </div>
      )}
    </section>
  );
}

// iOS has no colon key on the number pad: the colons are placed for the runner (race/timeInput.js).
export { formatGoalTime };

function RacePlanCta({
  race, raceLabel, raceDay = 'race day', showPlan, src, utmCampaign, donationLine, learnMoreHref, onLearnMore,
}) {
  const [goal, setGoal] = useState('');
  const [plan, setPlan] = useState(null);
  const [planError, setPlanError] = useState('');
  const [planLoading, setPlanLoading] = useState(false);

  const [form, setForm] = useState({ name: '', phone: '', tos: false, sms: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);

  const getPlan = async (e) => {
    e.preventDefault();
    setPlanError('');
    const g = goal.trim();
    if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(g)) {
      setPlanError('Try a time like 3:30 or 3:29:59.');
      return;
    }
    setPlanLoading(true);
    try {
      const res = await axios.get(`${getApiBaseUrl()}/api/public/race-plan`, { params: { race, goal: g } });
      setPlan(res.data);
      track('joke_plan_generated', { race, goal: g, src });
    } catch (err) {
      setPlan(null);
      setPlanError(err?.response?.data?.error || "Couldn't build that plan. Check the time and try again.");
      track('joke_plan_error', { race, goal: g, src, status: err?.response?.status });
    } finally {
      setPlanLoading(false);
    }
  };

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const signup = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!form.name.trim()) return setSubmitError('Add your first name.');
    if (form.phone.replace(/\D/g, '').length < 10) return setSubmitError('Add a mobile number we can text.');
    if (!form.tos || !form.sms) return setSubmitError('Check both boxes so your coach can text you.');
    setSubmitting(true);
    track('joke_signup_submit', { race, src, has_plan: !!plan });
    try {
      const res = await axios.post(`${getApiBaseUrl()}/api/public/register`, {
        name: form.name.trim(),
        phone_number: form.phone.trim(),
        sms_consent: form.sms,
        tos_privacy_consent: form.tos,
        health_data_consent: false,
        utm_source: 'shirt',
        utm_medium: 'qr',
        utm_campaign: utmCampaign,
      });
      if (res.data?.success) {
        setDone(true);
        track('joke_signup_success', { race, src, has_plan: !!plan });
      } else {
        setSubmitError(res.data?.error || 'Something went wrong. Try again.');
      }
    } catch (err) {
      const data = err?.response?.data || {};
      setSubmitError(data.error || 'Network error. Check your connection and try again.');
      track('joke_signup_error', { race, src, status: err?.response?.status, already: !!data.already_registered });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="race-cta">
      {showPlan && (
        <form className="race-goal" onSubmit={getPlan} noValidate>
          <label htmlFor="race-goal-input" className="race-goal-label">
            Running {raceLabel}? What&apos;s your goal time?
          </label>
          <div className="race-goal-row">
            <input
              id="race-goal-input"
              className="race-input"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="3:30"
              value={goal}
              onChange={(e) => setGoal(formatGoalTime(e.target.value))}
            />
            <button type="submit" className="race-btn" disabled={planLoading}>
              {planLoading ? 'Building...' : 'Get my race plan'}
            </button>
          </div>
          {planError && <p className="race-error" role="alert">{planError}</p>}
        </form>
      )}

      {plan && <PlanCard plan={plan} />}

      {done ? (
        <p className="race-done" role="status">
          Done. Check your texts, your coach just said hi.
        </p>
      ) : (
        <form className="race-signup" onSubmit={signup} noValidate>
          <p className="race-signup-head">
            {showPlan
              ? `Want a coach for what comes after ${raceDay}?`
              : `Just ran ${raceLabel}? Your next one starts with a coach.`}
          </p>
          {/* TRUE as written, and verified in the code: /api/public/register takes no card,
              onboarding + the first plan + _FREE_COACH_TURNS coach replies are free, and the
              price is shown at the trial ask, BEFORE any card is entered. Nothing on this path
              can auto-charge, so no price is needed here. If signup ever starts collecting a
              card, this line must change in the same commit. */}
          <p className="race-signup-free">Free to start. No card needed.</p>
          <div className="race-signup-fields">
            <input
              className="race-input"
              type="text"
              name="name"
              autoComplete="given-name"
              placeholder="First name"
              aria-label="First name"
              value={form.name}
              onChange={setField('name')}
            />
            <input
              className="race-input"
              type="tel"
              name="phone"
              autoComplete="tel"
              placeholder="Mobile number"
              aria-label="Mobile number"
              value={form.phone}
              onChange={setField('phone')}
            />
          </div>
          {/* Both labels are Onboarding.jsx's, word for word. Do not paraphrase: on this path the
              checkbox is the consent. */}
          <label className="race-check">
            <input type="checkbox" checked={form.tos} onChange={setField('tos')} />
            <span>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </span>
          </label>
          <label className="race-check">
            <input type="checkbox" checked={form.sms} onChange={setField('sms')} />
            <span>
              I consent to receive workout reminders, updates, and access to our AI text message agent via text message.
              Message and data rates may apply. You can opt out at any time by replying STOP.{' '}
              <a href="/privacy#sms" target="_blank" rel="noopener noreferrer">
                Learn more about SMS and text messaging
              </a>.
            </span>
          </label>
          <button type="submit" className="joke-cta-btn race-signup-btn" disabled={submitting}>
            {submitting ? 'Starting...' : 'Have my coach text me'}
          </button>
          {/* "Right away", not "a few quick questions": onboarding is a real conversation,
              and promising it's short would be the false-proximity copy we don't ship. */}
          <p className="race-fine">Your coach texts you right away to set up your plan.</p>
          {submitError && <p className="race-error" role="alert">{submitError}</p>}
          {donationLine && <p className="race-fine">{donationLine}</p>}
        </form>
      )}

      {/* Evaluate-first path for the visitor who won't give a number to a QR code yet: the
          same tracked landing-page link the regular /joke CTA carries (utm -> landing_page_visits). */}
      {learnMoreHref && (
        <a className="joke-learnmore" href={learnMoreHref} onClick={onLearnMore}>
          New to Mattr? See what it does &rarr;
        </a>
      )}
    </div>
  );
}

PlanCard.propTypes = {
  // Shape of GET /api/public/race-plan (utils/race_plan.build_public_race_plan).
  plan: PropTypes.shape({
    race: PropTypes.shape({ name: PropTypes.string, distance_mi: PropTypes.number }).isRequired,
    plan: PropTypes.shape({ finish: PropTypes.string, avg_pace: PropTypes.string }).isRequired,
    segments: PropTypes.arrayOf(PropTypes.object).isRequired,
    checkpoints: PropTypes.arrayOf(PropTypes.object).isRequired,
    landmarks: PropTypes.arrayOf(PropTypes.shape({
      mile: PropTypes.number, label: PropTypes.string, note: PropTypes.string, clock: PropTypes.string,
    })),
    fuel: PropTypes.object,
    course: PropTypes.object,
  }).isRequired,
};

RacePlanCta.propTypes = {
  race: PropTypes.string.isRequired,
  raceLabel: PropTypes.string.isRequired,
  showPlan: PropTypes.bool,
  src: PropTypes.string,
  utmCampaign: PropTypes.string.isRequired,
  donationLine: PropTypes.string,
  raceDay: PropTypes.string,
  learnMoreHref: PropTypes.string,
  onLearnMore: PropTypes.func,
};

export default RacePlanCta;
